import React, { Component } from 'react';
import { connect } from 'react-redux';

import { getOpenDate } from '@/store/course/actions';

import { selectCourse as courseSelect } from '@/store/course/selectors';
import { selectStudyPlan as studyPlanSelect } from '@/store/study-plan/selectors';
import { selectTopics as topicsSelect } from '@/store/topics/selectors';
import { selectTracks } from '@/store/tracks/selectors';
import { selectUserTopics as userTopicsSelect } from '@/store/user-topics/selectors';
import { selectUsers as usersSelect } from '@/store/users/selectors';

import { Modal, Skeleton } from 'antd';

import CalendarTableModal from './CalendarModal';
import CalendarModalData from './CalendarModalData';
import ConfirmModal from './ConfirmModal';
import EventModal from './EventModal';

import {
  isCorpCourseWithOrWithoutApprove,
  isCorporateCourseWithoutApprove,
  isCorpOrExtCourse,
  isEvent,
  isEventOrConference,
  isExternalCourse,
} from '@shared/utils/topic-utils';

import Utils from '@/Utils';
import { createSelector } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import css from './CalendarModal.module.scss';

class CalendarModal extends Component {
  currentYear = Number(dayjs().format('YYYY'));

  constructor(props) {
    super(props);
    this.state = {
      selectedYear: this.currentYear,
      visibleModalData: !props.hasEvents,
      hasEventsFlag: !!props.hasEvents,
      independent: true,
      showCalendarTableModal: false,
      comment: props.comment || '',
    };
  }

  isOnline = () => Utils.isOnline();

  componentDidMount() {
    if (!this.isOnline()) return;

    this.props.getOpenDate();
  }

  componentDidUpdate(prevProps) {
    const { open, hasEvents, topicInfo } = this.props;
    if (open && open !== prevProps.open) {
      this.setState({
        selectedYear: this.currentYear,
        visibleModalData: !hasEvents,
        independent: topicInfo && topicInfo.independent,
        showCalendarTableModal: false,
      });
    }
  }

  handleCalendarModalData = ({ hasEventsFlag, comment }) => {
    if (this.state.showCalendarTableModal) {
      this.setState({
        showCalendarTableModal: false,
        comment,
      });
    } else {
      this.setState({
        hasEventsFlag: hasEventsFlag,
        visibleModalData: !this.state.visibleModalData,
      });
    }
  };

  scheduleButtonHandler = ({ comment }) => {
    this.setState({
      comment,
      showCalendarTableModal: true,
      hasEventsFlag: true,
    });
  };

  render() {
    const {
      open,
      onCancel,
      disabledSelect,
      dateEvent,
      eventTypeId,
      selectMonthEvents,
      hasEvents,
      openDate,
      isLoading,
      toCalendar,
      dates,
    } = this.props;

    const { visibleModalData, showCalendarTableModal } = this.state;

    const year = !disabledSelect ? this.state.selectedYear.toString() : dayjs(dateEvent).format('YYYY') || '';

    const currentDates = [];

    const modalWidth = isEvent(eventTypeId) ? 470 : 600;

    const modalCondition = !showCalendarTableModal && isExternalCourse(eventTypeId) && (visibleModalData || toCalendar);

    if (dates && Array.isArray(dates)) {
      dates.forEach(item => {
        if (item.dates) {
          if (Array.isArray(item.dates)) {
            item.dates.forEach(date => {
              currentDates.push({ date, endTime: item.endTime, startTime: item.startTime });
            });
          }

          if (typeof item.dates === 'string') {
            currentDates.push({ date: item.dates });
          }
        }
      });
    }

    return (
      <Modal
        open={open}
        onCancel={onCancel}
        width={modalCondition ? 800 : modalWidth}
        footer={null}
        headerFixToModal
        centered
        closable={!isEventOrConference(eventTypeId)}
      >
        <Skeleton loading={isLoading} className={css['Skeleton']}>
          {!isCorpOrExtCourse(eventTypeId) &&
            !isEventOrConference(eventTypeId) &&
            !isCorporateCourseWithoutApprove(eventTypeId) && (
              <CalendarTableModal
                {...this.props}
                key='first-modal'
                noEvent
                openDate={openDate}
                isLoadingPlan={isLoading}
                selectMonthEvents={selectMonthEvents}
                dayPicker={true}
              />
            )}
          {isEventOrConference(eventTypeId) && open && (
            <ConfirmModal
              {...this.props}
              key='second-modal'
              eventTypeId={eventTypeId}
              comment={this.props.comment}
              isLoadingPlan={isLoading}
              dates={currentDates}
            />
          )}
          {((isCorpCourseWithOrWithoutApprove(eventTypeId) && (visibleModalData || toCalendar)) ||
            showCalendarTableModal) && (
            <CalendarTableModal
              {...this.props}
              key='third-modal'
              comment={this.props.comment || this.state.comment}
              openDate={this.state.independent ? dayjs().format('YYYY-MM-DD') : openDate}
              courses
              planInfo={this.props.planInfo}
              isLoadingPlan={this.props.isLoading}
              visibleModalData={visibleModalData}
              dateEvent={year}
              toPrev={this.handleCalendarModalData}
              hasEventsFlag={this.state.hasEventsFlag}
              showPrevLink={showCalendarTableModal}
              hasEvents={hasEvents}
              selectMonthEvents={selectMonthEvents}
              dayPicker={false}
              minTrackDate={this.props?.minTrackDate}
            />
          )}
          {modalCondition && (
            <CalendarModalData
              {...this.props}
              key='fourth-modal'
              comment={this.props.comment || this.state.comment}
              openDate={this.state.independent ? dayjs().format('YYYY-MM-DD') : openDate}
              isLoadingPlan={this.props.isLoading}
              visibleModalData={visibleModalData}
              planInfo={this.props.planInfo}
              dateEvent={year}
              toPrev={this.handleCalendarModalData}
              hasEventsFlag={this.state.hasEventsFlag}
              hasEvents={hasEvents}
              scheduleButtonHandler={this.scheduleButtonHandler}
            />
          )}
          {!toCalendar &&
            (isCorpOrExtCourse(eventTypeId) || isCorporateCourseWithoutApprove(eventTypeId)) &&
            !visibleModalData && (
              <EventModal
                isLoadingPlan={isLoading}
                planInfo={this.props.planInfo}
                handleCalendarModalData={this.handleCalendarModalData}
                {...this.props}
              />
            )}
        </Skeleton>
      </Modal>
    );
  }
}

const mapStateToProps = createSelector(
  [studyPlanSelect, topicsSelect, userTopicsSelect, courseSelect, usersSelect, selectTracks],
  (plan, topics, users, course, userTopics, tracks) => ({
    isLoading:
      plan.isLoading || topics.isLoading || userTopics.isLoading || tracks.isLoading || tracks.isReschedulingTrack,
    openDate: (course.openDate && course.openDate.openDate) || null,
    domainCompany: users.currentUser && users.currentUser.domainCompany,
  })
);

const mapActionsToProps = {
  getOpenDate,
};

CalendarModal.propTypes = {
  getOpenDate: PropTypes.func,
  comment: PropTypes.string,
  dateEvent: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  disabledSelect: PropTypes.bool,
  domainCompany: PropTypes.any,
  hasEvents: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  headerFixToModal: PropTypes.bool,
  isLoading: PropTypes.bool,
  noComment: PropTypes.bool,
  okText: PropTypes.string,
  onCancel: PropTypes.any,
  onOk: PropTypes.any,
  openDate: PropTypes.any,
  planInfo: PropTypes.any,
  planningTopicId: PropTypes.number,
  rest: PropTypes.any,
  title: PropTypes.string,
  toCalendar: PropTypes.bool,
  topicInfo: PropTypes.any,
  open: PropTypes.bool,
  eventTypeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectMonthEvents: PropTypes.bool,
  currentUser: PropTypes.object,
  dates: PropTypes.any,
};
export default connect(mapStateToProps, mapActionsToProps)(CalendarModal);
